<docs>
# FlashMessages
> Can spawn some flash messages

@author Nicolas Husson <nicolas@kffein.com>
</docs>

<script>
import { defineComponent, /* onMounted */ } from 'vue';
import { storeToRefs } from 'pinia'
import { store as flashStore } from './store';
import SvgClose from './assets/close.svg?raw';
import SvgStatusError from './assets/status-error.svg?raw';
import SvgStatusInfo from './assets/status-info.svg?raw';
import SvgStatusSuccess from './assets/status-success.svg?raw';
import SvgStatusWarning from './assets/status-warning.svg?raw';

const FlashMessages = defineComponent({
  name: "FlashMessages",
  setup() {
    const { queue } = storeToRefs(flashStore)

    const close = (id) => {
      flashStore.DELETE(id)
    };

    return {
      queue,
      close,
      SvgClose,
      SvgStatusError,
      SvgStatusInfo,
      SvgStatusSuccess,
      SvgStatusWarning,
    }
  },
});
export default FlashMessages;
</script>

<template>
  <div class="FlashMessages">
    <transition-group class="list"
                      name="list"
                      mode="out-in"
                      tag="ol"
    >
      <li v-for="{id, status, message, title, important} in queue"
          :key="`message-${id}`"
          class="item"
      >
        <article :data-status="status"
                 class="message"
        >
          <header class="header">
            <i v-if="status === 'error'"
               class="icon -status"
               v-html="SvgStatusError"
            />
            <i v-if="status === 'info'"
               class="icon -status"
               v-html="SvgStatusInfo"
            />
            <i v-if="status === 'success'"
               class="icon -status"
               v-html="SvgStatusSuccess"
            />
            <i v-if="status === 'warning'"
               class="icon -status"
               v-html="SvgStatusWarning"
            />
          </header>
          <div class="content">
            <p v-if="title"
               class="title"
            >
              <strong v-html="title" />
            </p>
            <p v-if="message"
               class="description"
               v-html="message"
            />
          </div>
          <button v-if="!important"
                  class="btn -close _no-btn"
                  aria-label="close"
                  @click.prevent="close(id)"
          >
            <i class="icon -close" v-html="SvgClose" />
          </button>
        </article>
      </li>
    </transition-group>
  </div>
</template>

<style lang="stylus" scoped>
  /**
   * TABLE OF CONTENT
   * --------
   *  - LAYOUT
   *  - DEBUG
   *  - NO_SCOPE
   */

  $flash-colors ?= {
    info: black,
    success: green,
    warning: orange,
    error: red,
  }

  //  ===LAYOUT===
  .list
    fixed bottom 20px right 20px
    z-index $z-flash
    display flex
    flex-direction column-reverse
    +mobile()
      fixed bottom 0 right 0 left 0
    >.item
      &:not(:first-child)
        margin-bottom 20px // column-reverse

  .message
    width 47vw
    min-width 400px
    max-width 700px
    +mobile()
      width 100%
      min-width 100%
      max-width 100%
    position relative
    background-color $c-blue
    color $c-white
    border-radius 10px
    padding 30px 24px
    overflow hidden
    box-shadow 0px 0px 9px rgba(#C9C9C9, 0.5)
    for status in ('info' 'success' 'warning' 'error')
      {'[data-status="'+status+'"]&'}
        background-color $flash-colors[status]
    +layout(desktop)
      f(row, $align:stretch)
      padding 48px 24px 

    .header
      rp(width, 60px 50px)
      flex auto 0 0
      f(row, $align: center, $justify: center)
      +layout(desktop)
        margin-right 35px
        margin-bottom 0
      +layout(mobile)
        margin-right 0
        margin-bottom 35px
        
    .icon.-status
      rp(size, 60px 50px)
      color $c-black

    .content
      flex 100% 1 1
    .title
      rp(font-size, 24px 18px)
      rp(margin-bottom, 8px 5px)
      font-weight $fw-semibold
      line-height 1.4
    .description
      rp(font-size, 17px 15px)
      line-height 1.2
      
      :deep()
        strong
          font-weight $fw-bold
        em
          font-style italic
        a
          text-decoration underline

    .btn.-close
      absolute top 20px right 20px
      size 18px
      box-sizing content-box
      border none
      background none
      outline none
      cursor pointer
      .icon
        display block
        size 100%
        stroke $c-white
        stroke-linecap round
        stroke-linejoin round
        stroke-width 1px
      .no-touch &
        transform rotate(0deg)
        trans(transform, $ease: easing('in-out-back'))
        &:hover
          transform rotate(90deg)

  .list-leave-active
  .list-enter-active
    transition 0.3s
  .list-enter-active
    transition-delay .3s
  .list-enter-from
    transform translateX(100%)
    opacity 0
  .list-leave-to
    transform translateX(100%)
    opacity 0

  //  ===DEBUG===
  [data-debug-mode="true"] .FlashMessages
    // fixed 0
    // background url('/_DEBUG/flash.jpg') left top no-repeat
    // background-color yellow
    //
</style>
